import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '../components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import { Badge } from '../components/ui/badge';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import {
  TrendingUp, Plus, Download, MoreVertical, Mail, MessageCircle, Send,
  CheckCircle, Trash2, Loader2, FileText, ArrowUpCircle, ArrowDownCircle, Pencil,
  FileSignature, Upload, FileCheck2,
} from 'lucide-react';
import { toast } from 'sonner';
import { rentIncreasesAPI, transactionsAPI, propertiesAPI, clientsAPI } from '../lib/api';
import { formatCurrency, formatDate } from '../lib/utils';

const REASONS = [
  { value: 'reajustement_annuel', label: 'Réajustement annuel' },
  { value: 'icc', label: 'Indexation ICC' },
  { value: 'renovation', label: 'Travaux de rénovation' },
  { value: 'mise_a_jour_marche', label: 'Mise à jour selon le marché' },
  { value: 'fin_de_bail', label: 'Renouvellement de bail' },
  { value: 'autre', label: 'Autre motif' },
];

const STATUS_STYLES = {
  brouillon: 'badge-draft',
  notifie: 'badge-partial',
  applique: 'badge-validated',
  annule: 'badge-unpaid',
};
const STATUS_LABELS = {
  brouillon: 'Brouillon',
  notifie: 'Notifié',
  applique: 'Appliqué',
  annule: 'Annulé',
};

const todayIso = () => new Date().toISOString().split('T')[0];
const plusMonthsIso = (months) => {
  const d = new Date();
  d.setMonth(d.getMonth() + months);
  return d.toISOString().split('T')[0];
};

const RentIncreases = () => {
  const [increases, setIncreases] = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [properties, setProperties] = useState([]);
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [uploadingFor, setUploadingFor] = useState(null);
  const uploadInputRef = useRef(null);
  const pendingUploadRecId = useRef(null);

  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({
    transaction_id: '',
    mode: 'percentage', // 'percentage' | 'amount'
    increase_percentage: '10',
    new_rent: '',
    effective_date: plusMonthsIso(3),
    reason: 'reajustement_annuel',
    reason_details: '',
    contract_clause: '',
    notes: '',
  });

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [iRes, tRes, pRes, cRes] = await Promise.all([
        rentIncreasesAPI.getAll(),
        transactionsAPI.getAll({ transaction_type: 'location' }),
        propertiesAPI.getAll({ transaction_type: 'location' }),
        clientsAPI.getAll(),
      ]);
      setIncreases(iRes.data || []);
      setTransactions(tRes.data || []);
      setProperties(pRes.data || []);
      setClients(cRes.data || []);
    } catch (e) {
      toast.error('Erreur lors du chargement');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  // Active leases only
  const activeLeases = useMemo(
    () => transactions.filter(t => !['resilie', 'termine', 'annule', 'expire'].includes(t.status)),
    [transactions]
  );

  // Enriched leases with tenant + property
  const leaseOptions = useMemo(() => {
    return activeLeases.map(t => {
      const tenant = clients.find(c => c.id === t.client_id);
      const property = properties.find(p => p.id === t.property_id);
      const rent = t.monthly_rent || t.amount || 0;
      return {
        ...t,
        tenant_name: tenant?.name || `${tenant?.first_name || ''} ${tenant?.last_name || ''}`.trim() || 'Locataire',
        property_title: property?.title || 'Bien',
        rent,
      };
    });
  }, [activeLeases, clients, properties]);

  const selectedLease = useMemo(
    () => leaseOptions.find(l => l.id === form.transaction_id),
    [leaseOptions, form.transaction_id]
  );

  // Compute preview (supports increases AND decreases)
  const preview = useMemo(() => {
    if (!selectedLease) return null;
    const current = selectedLease.rent;
    let newRent, increase, pct;
    if (form.mode === 'percentage') {
      pct = parseFloat(form.increase_percentage) || 0;
      increase = current * (pct / 100);
      newRent = current + increase;
    } else {
      newRent = parseFloat(form.new_rent) || 0;
      increase = newRent - current;
      pct = current > 0 ? (increase / current) * 100 : 0;
    }
    const direction = increase > 0 ? 'augmentation' : increase < 0 ? 'baisse' : 'identique';
    return {
      current,
      newRent: Math.round(newRent),
      increase: Math.round(increase),
      pct: Math.round(pct * 10) / 10,
      direction,
      valid: newRent >= 0 && Math.abs(increase) > 0 && newRent !== current,
    };
  }, [selectedLease, form.mode, form.increase_percentage, form.new_rent]);

  const resetForm = () => {
    setForm({
      transaction_id: '',
      mode: 'percentage',
      increase_percentage: '10',
      new_rent: '',
      effective_date: plusMonthsIso(3),
      reason: 'reajustement_annuel',
      reason_details: '',
      contract_clause: '',
      notes: '',
    });
    setEditing(null);
  };

  const openCreate = () => {
    resetForm();
    setShowModal(true);
  };

  const openEdit = (rec) => {
    setEditing(rec);
    setForm({
      transaction_id: rec.transaction_id,
      mode: 'amount',
      increase_percentage: String(rec.increase_percentage),
      new_rent: String(rec.new_rent),
      effective_date: rec.effective_date ? rec.effective_date.split('T')[0] : plusMonthsIso(3),
      reason: rec.reason || 'reajustement_annuel',
      reason_details: rec.reason_details || '',
      contract_clause: rec.contract_clause || '',
      notes: rec.notes || '',
    });
    setShowModal(true);
  };

  const handleSubmit = async () => {
    if (!form.transaction_id) { toast.error('Sélectionnez un bail'); return; }
    if (!preview?.valid) { toast.error('Le nouveau loyer doit être différent du loyer actuel'); return; }
    setSubmitting(true);
    try {
      const payload = {
        transaction_id: form.transaction_id,
        effective_date: form.effective_date ? new Date(form.effective_date).toISOString() : null,
        reason: form.reason,
        reason_details: form.reason_details || null,
        contract_clause: form.contract_clause || null,
        notes: form.notes || null,
      };
      if (form.mode === 'percentage') {
        payload.increase_percentage = parseFloat(form.increase_percentage);
      } else {
        payload.new_rent = parseFloat(form.new_rent);
      }

      let res;
      if (editing) {
        res = await rentIncreasesAPI.update(editing.id, payload);
      } else {
        res = await rentIncreasesAPI.create(payload);
      }
      const rec = res.data;
      toast.success(editing ? 'Augmentation mise à jour' : 'Brouillon créé');
      // Download avenant PDF + courrier PDF
      try {
        // Avenant (formal amendment)
        const avenant = await rentIncreasesAPI.downloadAvenant(rec.id);
        const aUrl = window.URL.createObjectURL(new Blob([avenant.data]));
        const aLink = document.createElement('a');
        aLink.href = aUrl;
        aLink.download = `avenant_bail_${rec.reference}.pdf`;
        document.body.appendChild(aLink);
        aLink.click();
        aLink.remove();
        window.URL.revokeObjectURL(aUrl);
        // Information letter
        const pdf = await rentIncreasesAPI.downloadPdf(rec.id);
        const url = window.URL.createObjectURL(new Blob([pdf.data]));
        const a = document.createElement('a');
        a.href = url;
        a.download = `courrier_modification_loyer_${rec.reference}.pdf`;
        document.body.appendChild(a);
        a.click();
        a.remove();
        window.URL.revokeObjectURL(url);
      } catch (err) {
        if (process.env.NODE_ENV === 'development') console.error('rent-increase PDF download failed', err);
      }
      setShowModal(false);
      resetForm();
      fetchData();
    } catch (e) {
      const detail = e.response?.data?.detail;
      const msg = Array.isArray(detail) ? detail.map(d => d.msg).join(', ') : (detail || 'Erreur');
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  const handleDownload = async (rec) => {
    try {
      const pdf = await rentIncreasesAPI.downloadPdf(rec.id);
      const url = window.URL.createObjectURL(new Blob([pdf.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = `courrier_augmentation_loyer_${rec.reference}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      if (process.env.NODE_ENV === 'development') console.error('handleDownload failed', err);
      toast.error('Erreur de téléchargement');
    }
  };

  const handleDownloadAvenant = async (rec) => {
    try {
      const pdf = await rentIncreasesAPI.downloadAvenant(rec.id);
      const url = window.URL.createObjectURL(new Blob([pdf.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = `avenant_bail_${rec.reference}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Avenant téléchargé');
    } catch (err) {
      if (process.env.NODE_ENV === 'development') console.error('avenant download failed', err);
      toast.error('Erreur de téléchargement de l\'avenant');
    }
  };

  const triggerUploadSigned = (rec) => {
    pendingUploadRecId.current = rec.id;
    if (uploadInputRef.current) {
      uploadInputRef.current.value = '';
      uploadInputRef.current.click();
    }
  };

  const handleFileSelected = async (e) => {
    const file = e.target.files?.[0];
    const recId = pendingUploadRecId.current;
    pendingUploadRecId.current = null;
    if (!file || !recId) return;
    setUploadingFor(recId);
    try {
      await rentIncreasesAPI.uploadSignedAvenant(recId, file);
      toast.success('Avenant signé téléversé · loyer appliqué au bail');
      fetchData();
    } catch (err) {
      if (process.env.NODE_ENV === 'development') console.error('signed avenant upload failed', err);
      toast.error(err.response?.data?.detail || 'Erreur lors du téléversement');
    } finally {
      setUploadingFor(null);
    }
  };

  const handleViewSignedAvenant = (rec) => {
    if (!rec.signed_avenant_url) return;
    const fullUrl = rec.signed_avenant_url.startsWith('http')
      ? rec.signed_avenant_url
      : `${process.env.REACT_APP_BACKEND_URL}${rec.signed_avenant_url}`;
    window.open(fullUrl, '_blank', 'noopener,noreferrer');
  };

  const handleDeleteSignedAvenant = async (rec) => {
    if (!window.confirm('Supprimer le scan de l\'avenant signé ? (Le bail garde son nouveau loyer)')) return;
    try {
      await rentIncreasesAPI.deleteSignedAvenant(rec.id);
      toast.success('Scan de l\'avenant supprimé');
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur');
    }
  };

  const handleNotify = async (rec) => {
    try {
      await rentIncreasesAPI.notify(rec.id);
      toast.success('Locataire notifié');
      fetchData();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur');
    }
  };

  const handleApply = async (rec) => {
    if (!window.confirm(`Appliquer immédiatement le nouveau loyer de ${formatCurrency(rec.new_rent, rec.currency)} au bail de ${rec.tenant_name} ?\nLe loyer du bail sera mis à jour et l'historique conservé.`)) return;
    try {
      await rentIncreasesAPI.apply(rec.id);
      toast.success('Nouveau loyer appliqué au bail');
      fetchData();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur');
    }
  };

  const handleDelete = async (rec) => {
    if (!window.confirm('Supprimer cette augmentation ?')) return;
    try {
      await rentIncreasesAPI.delete(rec.id);
      toast.success('Augmentation supprimée');
      fetchData();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur');
    }
  };

  const shareWhatsApp = (rec) => {
    const tenant = clients.find(c => c.id === rec.tenant_id);
    const phone = (tenant?.phone || '').replace(/[^\d]/g, '');
    const msg = encodeURIComponent(
      `Bonjour ${rec.tenant_name},\n\nNous vous informons que le loyer mensuel du bien "${rec.property_title}" sera révisé à ${formatCurrency(rec.new_rent, rec.currency)} (au lieu de ${formatCurrency(rec.current_rent, rec.currency)}, soit +${rec.increase_percentage.toFixed(1)}%) à compter du ${rec.effective_date ? formatDate(rec.effective_date) : 'la date d\'effet'}.\n\nUn courrier officiel vous a été remis.\n\nRéférence : ${rec.reference}`
    );
    const url = phone ? `https://wa.me/${phone}?text=${msg}` : `https://wa.me/?text=${msg}`;
    window.open(url, '_blank');
  };

  const shareEmail = (rec) => {
    const tenant = clients.find(c => c.id === rec.tenant_id);
    const subject = encodeURIComponent(`Notification d'augmentation de loyer - ${rec.reference}`);
    const body = encodeURIComponent(
      `Bonjour ${rec.tenant_name},\n\nNous vous informons que le loyer mensuel du bien "${rec.property_title}" sera révisé à ${formatCurrency(rec.new_rent, rec.currency)} (au lieu de ${formatCurrency(rec.current_rent, rec.currency)}, soit +${rec.increase_percentage.toFixed(1)}%) à compter du ${rec.effective_date ? formatDate(rec.effective_date) : 'la date d\'effet'}.\n\nUn courrier officiel vous a été remis.\n\nRéférence : ${rec.reference}\n\nCordialement.`
    );
    const to = tenant?.email || '';
    window.location.href = `mailto:${to}?subject=${subject}&body=${body}`;
  };

  return (
    <div className="space-y-6">
      <input
        ref={uploadInputRef}
        type="file"
        accept=".pdf,.jpg,.jpeg,.png,.webp"
        className="hidden"
        onChange={handleFileSelected}
        data-testid="signed-avenant-file-input"
      />
      <div className="flex items-start justify-between flex-wrap gap-4">
        <div>
          <h1 className="text-3xl font-bold text-[#1B3A4B] flex items-center gap-2">
            <FileSignature className="w-7 h-7 text-amber-600" />
            Modifications de Loyer
          </h1>
          <p className="text-gray-500 text-sm mt-1">
            Augmentations ou baisses de loyer · Courrier d&apos;information + Avenant officiel.
          </p>
        </div>
        <Button onClick={openCreate} className="btn-gold-premium" data-testid="new-rent-increase-btn">
          <Plus className="w-4 h-4" />
          Nouvelle modification
        </Button>
      </div>

      <Card className="glass">
        <CardHeader>
          <CardTitle className="text-base">Historique des modifications</CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex justify-center py-10"><Loader2 className="w-6 h-6 animate-spin text-gray-400" /></div>
          ) : increases.length === 0 ? (
            <div className="text-center py-10 text-gray-500">
              <FileSignature className="w-10 h-10 mx-auto text-gray-300 mb-2" />
              <p>Aucune modification enregistrée.</p>
              <p className="text-xs text-gray-400 mt-1">Cliquez sur &quot;Nouvelle modification&quot; pour commencer.</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="border-b border-gray-200">
                  <tr className="text-left text-xs uppercase tracking-wide text-gray-500">
                    <th className="py-2 px-2">Référence</th>
                    <th className="py-2 px-2">Locataire / Bien</th>
                    <th className="py-2 px-2 text-right">Ancien loyer</th>
                    <th className="py-2 px-2 text-right">Nouveau loyer</th>
                    <th className="py-2 px-2 text-right">Variation</th>
                    <th className="py-2 px-2">Date d&apos;effet</th>
                    <th className="py-2 px-2">Statut</th>
                    <th className="py-2 px-2 text-right">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {increases.map((rec) => (
                    <tr key={rec.id} className="row-premium border-b border-gray-100 text-sm" data-testid={`increase-row-${rec.id}`}>
                      <td className="py-3 px-2 font-mono text-xs">{rec.reference}</td>
                      <td className="py-3 px-2">
                        <div className="font-semibold text-[#1B3A4B]">{rec.tenant_name}</div>
                        <div className="text-xs text-gray-500">{rec.property_title}</div>
                      </td>
                      <td className="py-3 px-2 text-right tabular-nums">{formatCurrency(rec.current_rent, rec.currency)}</td>
                      <td className="py-3 px-2 text-right tabular-nums font-semibold text-emerald-700">{formatCurrency(rec.new_rent, rec.currency)}</td>
                      <td className="py-3 px-2 text-right tabular-nums">
                        <span className={`${rec.increase_amount < 0 ? 'text-red-700' : 'text-amber-700'} font-semibold`}>
                          {rec.increase_amount < 0 ? '-' : '+'}{formatCurrency(Math.abs(rec.increase_amount), rec.currency)}
                        </span>
                        <div className="text-xs text-gray-500">
                          {rec.increase_percentage >= 0 ? '+' : ''}{rec.increase_percentage.toFixed(1)}%
                          {rec.change_type === 'baisse' && (
                            <span className="ml-1 inline-flex items-center text-red-600">
                              <ArrowDownCircle className="w-3 h-3" />
                            </span>
                          )}
                          {rec.change_type === 'augmentation' && (
                            <span className="ml-1 inline-flex items-center text-emerald-600">
                              <ArrowUpCircle className="w-3 h-3" />
                            </span>
                          )}
                        </div>
                      </td>
                      <td className="py-3 px-2 text-xs">{rec.effective_date ? formatDate(rec.effective_date) : '-'}</td>
                      <td className="py-3 px-2">
                        <span className={`badge-status ${STATUS_STYLES[rec.status] || ''}`}>
                          {STATUS_LABELS[rec.status] || rec.status}
                        </span>
                        {rec.signed_avenant_url && (
                          <span className="badge-status badge-validated ml-1" title="Avenant signé téléversé" data-testid={`signed-badge-${rec.id}`}>
                            <FileCheck2 className="w-3 h-3" /> Signé
                          </span>
                        )}
                        {uploadingFor === rec.id && (
                          <span className="text-[10px] text-emerald-700 ml-1 inline-flex items-center gap-1">
                            <Loader2 className="w-3 h-3 animate-spin" /> Téléversement…
                          </span>
                        )}
                      </td>
                      <td className="py-3 px-2 text-right">
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="sm" data-testid={`increase-actions-${rec.id}`}>
                              <MoreVertical className="w-4 h-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem onClick={() => handleDownload(rec)}>
                              <Download className="w-4 h-4 mr-2" /> Télécharger le courrier
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => handleDownloadAvenant(rec)} className="text-[#1B3A4B] font-semibold" data-testid={`download-avenant-${rec.id}`}>
                              <FileSignature className="w-4 h-4 mr-2" /> Télécharger l&apos;avenant
                            </DropdownMenuItem>
                            {rec.status !== 'annule' && !rec.signed_avenant_url && (
                              <DropdownMenuItem onClick={() => triggerUploadSigned(rec)} className="text-emerald-700" data-testid={`upload-signed-${rec.id}`}>
                                <Upload className="w-4 h-4 mr-2" /> Téléverser l&apos;avenant signé
                              </DropdownMenuItem>
                            )}
                            {rec.signed_avenant_url && (
                              <>
                                <DropdownMenuItem onClick={() => handleViewSignedAvenant(rec)} className="text-emerald-700">
                                  <FileCheck2 className="w-4 h-4 mr-2" /> Voir l&apos;avenant signé
                                </DropdownMenuItem>
                                <DropdownMenuItem onClick={() => handleDeleteSignedAvenant(rec)} className="text-red-600">
                                  <Trash2 className="w-4 h-4 mr-2" /> Supprimer le scan
                                </DropdownMenuItem>
                              </>
                            )}
                            <DropdownMenuItem onClick={() => shareWhatsApp(rec)} className="text-green-700">
                              <MessageCircle className="w-4 h-4 mr-2" /> Partager WhatsApp
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => shareEmail(rec)} className="text-blue-700">
                              <Mail className="w-4 h-4 mr-2" /> Envoyer par email
                            </DropdownMenuItem>
                            {rec.status === 'brouillon' && (
                              <>
                                <DropdownMenuItem onClick={() => openEdit(rec)}>
                                  <Pencil className="w-4 h-4 mr-2" /> Modifier
                                </DropdownMenuItem>
                                <DropdownMenuItem onClick={() => handleNotify(rec)} className="text-blue-700" data-testid={`notify-${rec.id}`}>
                                  <Send className="w-4 h-4 mr-2" /> Marquer comme notifié
                                </DropdownMenuItem>
                              </>
                            )}
                            {rec.status !== 'applique' && rec.status !== 'annule' && (
                              <DropdownMenuItem onClick={() => handleApply(rec)} className="text-emerald-700" data-testid={`apply-${rec.id}`}>
                                <CheckCircle className="w-4 h-4 mr-2" /> Appliquer au bail
                              </DropdownMenuItem>
                            )}
                            {rec.status !== 'applique' && (
                              <DropdownMenuItem onClick={() => handleDelete(rec)} className="text-red-600">
                                <Trash2 className="w-4 h-4 mr-2" /> Supprimer
                              </DropdownMenuItem>
                            )}
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Create / Edit Modal */}
      <Dialog open={showModal} onOpenChange={(open) => { setShowModal(open); if (!open) resetForm(); }}>
        <DialogContent className="sm:max-w-[680px] max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileSignature className="w-5 h-5 text-amber-600" />
              {editing ? 'Modifier la modification de loyer' : 'Nouvelle modification de loyer'}
            </DialogTitle>
            <DialogDescription>
              Augmentation ou baisse. L&apos;avenant officiel + le courrier d&apos;information seront générés.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-2">
            {/* Lease selector */}
            <div>
              <Label>Bail concerné *</Label>
              <Select
                value={form.transaction_id}
                onValueChange={(v) => setForm({ ...form, transaction_id: v })}
                disabled={!!editing}
              >
                <SelectTrigger data-testid="rent-increase-lease-select">
                  <SelectValue placeholder="Sélectionnez un bail actif" />
                </SelectTrigger>
                <SelectContent>
                  {leaseOptions.length === 0 ? (
                    <SelectItem value="none" disabled>Aucun bail actif</SelectItem>
                  ) : leaseOptions.map((l) => (
                    <SelectItem key={l.id} value={l.id}>
                      {l.tenant_name} — {l.property_title} ({formatCurrency(l.rent, l.currency || 'XOF')}/mois)
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Mode + value */}
            {selectedLease && (
              <>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label>Mode</Label>
                    <Select
                      value={form.mode}
                      onValueChange={(v) => setForm({ ...form, mode: v })}
                    >
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="percentage">Pourcentage (+/- %)</SelectItem>
                        <SelectItem value="amount">Nouveau loyer (montant)</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  {form.mode === 'percentage' ? (
                    <div>
                      <Label>Pourcentage (%)</Label>
                      <Input
                        type="number"
                        step="0.1"
                        value={form.increase_percentage}
                        onChange={(e) => setForm({ ...form, increase_percentage: e.target.value })}
                        placeholder="+10 ou -5"
                        data-testid="rent-increase-percentage-input"
                      />
                      <p className="text-[11px] text-gray-500 mt-1">Saisissez une valeur négative pour une baisse (ex: -5).</p>
                    </div>
                  ) : (
                    <div>
                      <Label>Nouveau loyer ({selectedLease.currency || 'XOF'})</Label>
                      <Input
                        type="number"
                        value={form.new_rent}
                        onChange={(e) => setForm({ ...form, new_rent: e.target.value })}
                        placeholder={selectedLease.rent.toString()}
                        data-testid="rent-increase-newrent-input"
                      />
                    </div>
                  )}
                </div>

                {/* Live preview */}
                {preview && (
                  <div className={`p-3 rounded-lg border space-y-1 text-sm ${preview.direction === 'baisse' ? 'border-red-200 bg-red-50' : 'border-amber-200 bg-amber-50'}`}>
                    <div className="flex justify-between"><span className="text-gray-600">Loyer actuel</span><span className="font-semibold tabular-nums">{formatCurrency(preview.current, selectedLease.currency || 'XOF')}</span></div>
                    <div className="flex justify-between"><span className="text-gray-600">Nouveau loyer</span><span className={`font-bold tabular-nums ${preview.direction === 'baisse' ? 'text-red-700' : 'text-emerald-700'}`} data-testid="preview-new-rent">{formatCurrency(preview.newRent, selectedLease.currency || 'XOF')}</span></div>
                    <div className={`flex justify-between border-t pt-1 mt-1 ${preview.direction === 'baisse' ? 'border-red-200' : 'border-amber-200'}`}>
                      <span className="text-gray-700 font-semibold inline-flex items-center gap-1">
                        {preview.direction === 'baisse' ? <ArrowDownCircle className="w-4 h-4 text-red-600" /> : <ArrowUpCircle className="w-4 h-4 text-emerald-600" />}
                        {preview.direction === 'baisse' ? 'Baisse' : 'Augmentation'}
                      </span>
                      <span className={`font-bold tabular-nums ${preview.valid ? (preview.direction === 'baisse' ? 'text-red-700' : 'text-amber-700') : 'text-red-600'}`} data-testid="preview-increase">
                        {preview.increase >= 0 ? '+' : '-'}{formatCurrency(Math.abs(preview.increase), selectedLease.currency || 'XOF')} ({preview.pct >= 0 ? '+' : ''}{preview.pct.toFixed(1)}%)
                      </span>
                    </div>
                    {!preview.valid && (
                      <div className="text-xs text-red-600 pt-1">Le nouveau loyer doit être différent du loyer actuel et positif.</div>
                    )}
                  </div>
                )}
              </>
            )}

            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Date d&apos;effet</Label>
                <Input
                  type="date"
                  value={form.effective_date}
                  onChange={(e) => setForm({ ...form, effective_date: e.target.value })}
                  min={todayIso()}
                  data-testid="rent-increase-effective-date"
                />
                <p className="text-[11px] text-gray-500 mt-1">Préavis légal recommandé : 3 mois.</p>
              </div>
              <div>
                <Label>Motif</Label>
                <Select
                  value={form.reason}
                  onValueChange={(v) => setForm({ ...form, reason: v })}
                >
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {REASONS.map((r) => (
                      <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div>
              <Label>Motif détaillé (optionnel)</Label>
              <Input
                value={form.reason_details}
                onChange={(e) => setForm({ ...form, reason_details: e.target.value })}
                placeholder="Ex: indexation annuelle 2026, travaux salle de bain..."
              />
            </div>

            <div>
              <Label>Référence clause contractuelle (optionnel)</Label>
              <Input
                value={form.contract_clause}
                onChange={(e) => setForm({ ...form, contract_clause: e.target.value })}
                placeholder="Ex: Article 5 du contrat de bail"
              />
            </div>

            <div>
              <Label>Notes supplémentaires (optionnel)</Label>
              <Textarea
                rows={2}
                value={form.notes}
                onChange={(e) => setForm({ ...form, notes: e.target.value })}
                placeholder="Ajoutez un paragraphe complémentaire au courrier..."
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowModal(false)} disabled={submitting}>Annuler</Button>
            <Button
              onClick={handleSubmit}
              disabled={submitting || !preview?.valid}
              className="btn-gold-premium"
              data-testid="save-rent-increase-btn"
            >
              {submitting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <FileText className="w-4 h-4 mr-2" />}
              {editing ? 'Mettre à jour + télécharger' : 'Créer + télécharger l\'avenant et le courrier'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default RentIncreases;
